/**
 * TanStack Query hooks — Extractions (jobs Claude lus/lancés depuis le frontend).
 *
 * Routes API consommées (voir `apps/api/src/routes/extractions.ts`) :
 *   POST   /v1/extractions                       lance un job                    → 202
 *   GET    /v1/extractions/:id                   poll status + métadonnées
 *   GET    /v1/extractions?submission_id=…
 *   GET    /v1/extractions?intake_event_id=…     (9.T.5.v3.1)
 *   GET    /v1/extractions/:id/fields
 *
 * 9.T.5.v3.3 (audit PO Q3 + Q5 hard-break) :
 *   - Types `ExtractionRow` / `ExtractedFieldRow` / `StartExtractionBody`
 *     consommés depuis @kollectivia/shared (source unique).
 *   - Hooks parent-kind agnostiques via discriminant `ExtractionSource`.
 *   - Legacy `useExtractionsBySubmission` supprimé (hard-break interne, tous
 *     les consumers migrent vers `useExtractionsByParent`).
 *   - `useStartExtraction` envoie exclusivement la forme v3.1
 *     `{ source: { kind, id }, file_id, ... }` (fin de la forme legacy web).
 */

import type {
  DbPage,
  ExtractedFieldRow,
  ExtractionParentKind,
  ExtractionRow,
  ExtractionSource,
  ExtractionStatus,
} from "@kollectivia/shared";
import { useMutation, useQuery, useQueryClient, type QueryKey } from "@tanstack/react-query";

import { api } from "../api";

// Re-export shared types pour éviter chaque consumer d'importer @kollectivia/shared
// (compat interne — les types wire ne sont plus dupliqués localement).
export type { ExtractedFieldRow, ExtractionRow, ExtractionStatus } from "@kollectivia/shared";

// -----------------------------------------------------------------------------
// Params + input types
// -----------------------------------------------------------------------------

export type ExtractionListParams = {
  cursor?: string | null;
  limit?: number;
  submission_id?: string;
  intake_event_id?: string;
  status?: ExtractionStatus;
  pass_id?: string;
};

/**
 * v3.3 hard-break — le frontend n'émet plus que la forme discriminée v3.1
 * `{ source, file_id, ... }`. La forme legacy `{ submission_id, ... }` reste
 * acceptée côté API pour compat backward externe (MCP, api_keys) mais AUCUN
 * consumer web ne l'utilise plus.
 */
export type StartExtractionInput = {
  source: ExtractionSource;
  file_id: string;
  pass_id?: string;
  model_id?: string;
};

// -----------------------------------------------------------------------------
// Query keys
// -----------------------------------------------------------------------------

export const extractionsKeys = {
  all: ["extractions"] as const,
  list: (params: ExtractionListParams) =>
    ["extractions", "list", params] as const satisfies QueryKey,
  byParent: (parentKind: ExtractionParentKind, parentId: string) =>
    [
      "extractions",
      "list",
      { parent_kind: parentKind, parent_id: parentId },
    ] as const satisfies QueryKey,
  detail: (id: string) => ["extractions", "detail", id] as const satisfies QueryKey,
  fields: (id: string) => ["extractions", "fields", id] as const satisfies QueryKey,
};

// -----------------------------------------------------------------------------
// Builders
// -----------------------------------------------------------------------------

function buildListPath(params: ExtractionListParams): string {
  const sp = new URLSearchParams();
  if (params.cursor) sp.set("cursor", params.cursor);
  if (params.limit) sp.set("limit", String(params.limit));
  if (params.submission_id) sp.set("submission_id", params.submission_id);
  if (params.intake_event_id) sp.set("intake_event_id", params.intake_event_id);
  if (params.status) sp.set("status", params.status);
  if (params.pass_id) sp.set("pass_id", params.pass_id);
  const qs = sp.toString();
  return qs ? `/v1/extractions?${qs}` : "/v1/extractions";
}

function isTerminal(status: ExtractionStatus | undefined): boolean {
  return status === "done" || status === "failed";
}

// -----------------------------------------------------------------------------
// Reads
// -----------------------------------------------------------------------------

export function useExtractions(params: ExtractionListParams) {
  return useQuery({
    queryKey: extractionsKeys.list(params),
    queryFn: () => api.get<DbPage<ExtractionRow>>(buildListPath(params)),
    placeholderData: (prev) => prev,
  });
}

/**
 * Lecture parent-agnostique — 9.T.5.v3.3 (remplace `useExtractionsBySubmission`).
 * Le discriminant `parentKind` détermine la clé query param envoyée au listing.
 */
export function useExtractionsByParent(
  parentKind: ExtractionParentKind,
  parentId: string | undefined,
  opts: { pollIntervalMs?: number } = {},
) {
  const effectiveId = parentId ?? "__none__";
  const { pollIntervalMs = 0 } = opts;
  return useQuery({
    queryKey: extractionsKeys.byParent(parentKind, effectiveId),
    queryFn: () =>
      api.get<DbPage<ExtractionRow>>(
        buildListPath({
          [parentKind === "submission" ? "submission_id" : "intake_event_id"]: effectiveId,
          limit: 50,
        } as ExtractionListParams),
      ),
    enabled: !!parentId,
    // 9.T.5.v3.6 QA post-fix #5 — polling optionnel tant qu'aucune extraction
    // `done` ni `failed`. Le caller (Step4ExtractedData) active pollIntervalMs
    // après clic « Lancer l'extraction » pour rafraîchir automatiquement quand
    // le job async Claude termine. Stop dès qu'au moins une extraction est
    // terminale — pas de polling infini.
    refetchInterval: (query) => {
      if (!pollIntervalMs) return false;
      const data = query.state.data as DbPage<ExtractionRow> | undefined;
      if (!data) return pollIntervalMs;
      const anyTerminal = data.items.some((e) => isTerminal(e.status));
      return anyTerminal ? false : pollIntervalMs;
    },
    refetchOnWindowFocus: false,
  });
}

/**
 * Lecture d'un job avec polling optionnel. Si `pollIntervalMs > 0`,
 * TanStack relance la query à cet intervalle TANT QUE le statut n'est pas
 * terminal. Une fois `done` ou `failed`, le polling s'arrête automatiquement
 * (refetchInterval renvoie `false`).
 */
export function useExtraction(
  id: string | undefined,
  opts: { pollIntervalMs?: number; enabled?: boolean } = {},
) {
  const { pollIntervalMs = 0, enabled = true } = opts;
  return useQuery({
    queryKey: id ? extractionsKeys.detail(id) : extractionsKeys.detail("__none__"),
    queryFn: () => api.get<ExtractionRow>(`/v1/extractions/${id}`),
    enabled: !!id && enabled,
    refetchInterval: (query) => {
      if (!pollIntervalMs) return false;
      const data = query.state.data as ExtractionRow | undefined;
      if (!data) return pollIntervalMs;
      return isTerminal(data.status) ? false : pollIntervalMs;
    },
    // Désactive le refetch on focus pendant un poll actif — sinon la
    // fenêtre devient bavarde sans bénéfice.
    refetchOnWindowFocus: false,
  });
}

export function useExtractionFields(id: string | undefined) {
  return useQuery({
    queryKey: id ? extractionsKeys.fields(id) : extractionsKeys.fields("__none__"),
    queryFn: () =>
      api.get<{ items: ExtractedFieldRow[]; total: number }>(`/v1/extractions/${id}/fields`),
    enabled: !!id,
  });
}

// -----------------------------------------------------------------------------
// Writes
// -----------------------------------------------------------------------------

/**
 * Lance un job d'extraction — 9.T.5.v3.3 parent-agnostique.
 * L'API répond 202 avec la row initiale (status='pending'). Le client poll
 * ensuite via `useExtraction`.
 *
 * Invalidations onSuccess dispatchent par parent kind :
 *   - `extractionsKeys.byParent(source.kind, source.id)` (liste review)
 *   - `["submissions", "detail", source.id]` OU `["intake-events", "detail", source.id]`
 *     — le détail parent inclut `last_extraction` qu'il faut rafraîchir
 */
export function useStartExtraction() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: StartExtractionInput) =>
      api.post<ExtractionRow>("/v1/extractions", input as unknown as Record<string, unknown>),
    onSuccess: (row, vars) => {
      const parentKind = vars.source.kind;
      const parentId = vars.source.id;
      void qc.invalidateQueries({
        queryKey: extractionsKeys.byParent(parentKind, parentId),
      });
      const detailKey =
        parentKind === "submission"
          ? ["submissions", "detail", parentId]
          : ["intake-events", "detail", parentId];
      void qc.invalidateQueries({ queryKey: detailKey });
      // Silence l'unused param warning quand `row` n'est pas consommé (le composant caller lit le hook).
      void row;
    },
  });
}
